import React from 'react';
import {useNavigate} from 'react-router-dom';
import {ComposedChart, Line, Area, XAxis, ResponsiveContainer} from 'recharts';
import Icon from '@/shared/components/Icon';
import {filterTestsByCategory, HEALTH_CATEGORIES} from '@/modules/patient-dashboard/utils/healthCategories';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import {determineStatus, cleanBiomarkerName, parseNumericValue, isTestNotPerformed} from '@/shared/utils/biomarkerUtils';
import {resolveDisplayRule} from '@/shared/biomarkers/resolveDisplayRule';
import {ruleToConfig} from '@/shared/biomarkers/ruleToConfig';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';
import {BiomarkerStatus} from '@/shared/types/biomarker';
import {getStatusClass, getStatusColor} from '@/shared/constants/colors';
import {severityTier} from '@/shared/biomarkers/classify';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {
    buildHistoryPointsFromResults,
    buildTestingHistoryChartData,
    buildDesignGridData,
} from '@/shared/utils/resultUtils';

interface BiomarkerDetailChartProps {
    selectedCategory: string;
    onBiomarkerSelect?: (biomarkerName: string) => void;
    selectedBiomarkerName?: string | null;
    /** Admin-managed display rules (see useDisplayRules); defaults to none. */
    rules?: BiomarkerDisplayRule[];
}

// Helper function to get status text with custom label support
const getStatusText = (
    status: BiomarkerStatus,
    testName: string | undefined,
    gender: 'male' | 'female' | undefined,
    rules: BiomarkerDisplayRule[],
    loinc?: string | null,
    assayCode?: string | null
): string => {
    // If test name is provided, check for a matching admin-managed display rule
    if (testName) {
        const rule = resolveDisplayRule(rules, {loinc, assayCode, testName, sex: gender});
        const customConfig = rule ? ruleToConfig(rule) : null;
        if (customConfig && customConfig.zones.length > 0) {
            // Find the zone that matches this status
            const matchingZone = customConfig.zones.find((zone) => zone.status === status);
            if (matchingZone) {
                return matchingZone.label;
            }
        }
    }

    // Fall back to default labels
    switch (status) {
        case 'Normal':
        case 'Optimal':
            return 'Optimal';
        case 'High':
            return 'High';
        case 'Low':
            return 'Low';
        case 'Borderline':
            return 'Borderline';
        case 'Elevated':
            return 'Elevated';
        default:
            return 'Unknown';
    }
};

// Helper function to get chart gradient color based on status
const getChartGradient = (status: BiomarkerStatus, index: number): string => {
    switch (status) {
        case 'Normal':
        case 'Optimal':
        case 'Negative': // Negative disease markers (good result)
            return `heartHealthGradient-normal-${index}`; // Green
        case 'High':
        case 'Positive': // Positive disease markers (elevated)
        case 'Toxic': // Toxic levels (e.g., high Vitamin D)
            return `heartHealthGradient-high-${index}`; // Red
        case 'Low':
        case 'Elevated':
        case 'Deficient': // Vitamin/mineral deficiencies
        case 'Impaired': // Impaired function (e.g., low eGFR)
            return `heartHealthGradient-low-${index}`; // Orange
        case 'Borderline':
            return `heartHealthGradient-borderline-${index}`; // Yellow
        default:
            return `heartHealthGradient-unknown-${index}`; // Gray
    }
};

// Helper function to get chart gradient colors. Resolves through the canonical
// getStatusColor mapper so a tenant override of the green/orange/red brand
// tokens repaints these charts in lockstep with badges + bars, and every
// status's chart color always matches its chip/count color.
const getGradientColors = (status: BiomarkerStatus) => {
    const color = getStatusColor(status);
    return {primary: color, secondary: color};
};

const BiomarkerDetailChart: React.FC<BiomarkerDetailChartProps> = ({
    selectedCategory,
    onBiomarkerSelect,
    selectedBiomarkerName,
    rules = [],
}) => {
    const navigate = useNavigate();
    const {user} = useAuth();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Always the patient's real data — never gated by membership.
    const {data: currentPatientData} = useDashboardData();

    // Get all results for the current patient
    const allResults = React.useMemo(() => {
        if (!currentPatientData || !Array.isArray(currentPatientData)) {
            return [];
        }
        return currentPatientData.flatMap(
            (test: any) =>
                test.results?.map((result: any) => ({
                    ...result,
                    createdDate: test.createdDate,
                    amendedDate: test.amendedDate,
                    orderId: test.id,
                })) || []
        );
    }, [currentPatientData]);

    // Filter results by selected category and group by biomarker name
    const categoryResults = React.useMemo(() => {
        const filteredResults = filterTestsByCategory(allResults, selectedCategory);

        // Group by test_name to consolidate duplicates
        const groupedResults = filteredResults.reduce(
            (acc, result) => {
                const testName = result.test_name;
                if (!acc[testName]) {
                    acc[testName] = [];
                }
                acc[testName].push(result);
                return acc;
            },
            {} as Record<string, any[]>
        );

        // Return one biomarker per test name with real historical data when available
        return Object.entries(groupedResults).map(([, results]) => {
            // Sort by statusDate (biomarker timing), fallback to createdDate and get the most recent result as the main display
            const sortedResults = results.sort(
                (a, b) =>
                    new Date(b.statusDate || b.createdDate).getTime() -
                    new Date(a.statusDate || a.createdDate).getTime()
            );

            const mostRecentResult = sortedResults[0];

            // Use shared history shaping for consistency — always real history, never gated by membership.
            const historyPoints = buildHistoryPointsFromResults(results);
            const hasHistory = historyPoints.length > 0;
            const historicalData = hasHistory
                ? historyPoints.length > 1
                    ? historyPoints.slice(0, -1)
                    : historyPoints
                : [];

            return {
                ...mostRecentResult, // Most recent result
                historicalData: historicalData,
                hasHistory,
            };
        });
    }, [allResults, selectedCategory]);

    // Get category info
    const categoryInfo = HEALTH_CATEGORIES[selectedCategory];

    // Handle Results button click - notify parent or navigate to the test
    const handleResultsClick = (biomarker: any) => {
        // If onBiomarkerSelect is provided, call it to update Lab Results tile
        if (onBiomarkerSelect) {
            onBiomarkerSelect(biomarker.test_name);
            return;
        }

        // Otherwise, navigate to the full result page
        if (!currentPatientData || !Array.isArray(currentPatientData)) {
            return;
        }

        // Find the test that contains this biomarker
        const testWithBiomarker = currentPatientData.find((test: any) =>
            test.results?.some((result: any) => result.test_name === biomarker.test_name)
        );

        if (testWithBiomarker) {
            navigate(`/result/${testWithBiomarker.id}`);
        }
    };

    // Auto-select first biomarker when category changes
    // Use a ref to track the previous category to prevent loops
    const prevCategoryRef = React.useRef<string>(selectedCategory);
    React.useEffect(() => {
        // Only trigger when category actually changes, not on every render
        if (selectedCategory !== prevCategoryRef.current) {
            prevCategoryRef.current = selectedCategory;
            if (onBiomarkerSelect && categoryResults.length > 0) {
                // Check if the currently selected biomarker is already in this category
                // If so, don't auto-select (user selected this biomarker and we changed category to match)
                const selectedIsInCategory =
                    selectedBiomarkerName &&
                    categoryResults.some((b) => cleanBiomarkerName(b.test_name) === selectedBiomarkerName);

                if (!selectedIsInCategory) {
                    onBiomarkerSelect(categoryResults[0].test_name);
                }
            }
        }
    }, [selectedCategory, categoryResults, onBiomarkerSelect, selectedBiomarkerName]);

    // Show loading state if data is not yet available
    if (!currentPatientData) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">Loading biomarkers...</p>
            </div>
        );
    }

    if (!categoryInfo) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">Category not found</p>
            </div>
        );
    }

    if (categoryResults.length === 0) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">No biomarkers available for {categoryInfo.name}</p>
            </div>
        );
    }

    return (
        <div>
            {categoryResults.map((biomarker, index) => {
                // Use the same status determination logic as biomarkerUtils (with gender for gender-specific biomarkers)
                const status = determineStatus(biomarker, gender, rules);
                const statusText = getStatusText(
                    status,
                    biomarker.test_name,
                    gender,
                    rules,
                    biomarker.loinc,
                    biomarker.test_code
                );
                const chartGradientId = getChartGradient(status, index);
                const gradientColors = getGradientColors(status);

                // Status text color + badge shape both derive from the canonical
                // status→color / status→tier mappers so the label, badge, and
                // chart colors always agree (e.g. a Low label is now orange, not pink).
                const tier = severityTier(status);
                const statusTextClass = status === 'Unknown' ? 'text-mld-text-primary' : getStatusClass(status, 'text');
                const isOptimal = tier === 'normal';

                // Design-grid trend data: line runs to the chart borders with a
                // short lead-in, and the dot (latest reading) stays inset from
                // the right edge. Shared by both the desktop + mobile charts.
                const trendRaw = (() => {
                    const histData = biomarker.historicalData || [];
                    // null for a non-numeric result; the trend builder gaps the
                    // current reading and buildDesignGridData drops the null
                    // point, so no dot/flat line is drawn for it.
                    const currentValue = parseNumericValue(biomarker.result);
                    const cd = buildTestingHistoryChartData({
                        history: histData,
                        currentValue,
                        currentDate: biomarker.statusDate || biomarker.createdDate,
                        maxHistoryPoints: 3,
                    });
                    // Single-reading (no real history) → flat line at the value.
                    if (cd.length === 2 && cd[0].dummy && cd[1].value !== null) {
                        const flatValue = cd[1].value;
                        return [
                            {name: 'start', value: flatValue},
                            {name: 'end', value: flatValue},
                        ];
                    }
                    return cd;
                })();
                const trendGrid = buildDesignGridData(trendRaw);

                return (
                    <div
                        key={`${biomarker.test_name}-${index}`}
                        className="py-3 px-4 border-b border-gray-100 last:border-b-0 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleResultsClick(biomarker)}>
                        {/* Desktop Layout: Single Row */}
                        <div className="hidden md:grid grid-cols-[7rem_minmax(0,1fr)_5.5rem] gap-4 items-center min-w-0">
                            {/* Column 1: Test name + value */}
                            <div className="min-w-0">
                                <h4 className="text-xs font-medium text-mld-text-primary mb-0.5">
                                    {cleanBiomarkerName(biomarker.test_name)}
                                </h4>
                                <div className="flex items-baseline gap-1">
                                    {isTestNotPerformed(biomarker.result) ? (
                                        <span className="text-[17px] text-gray-400 leading-none">Not Tested</span>
                                    ) : (
                                        <span className="text-[24px] text-mld-text-primary leading-none">
                                            {(() => {
                                                const num = parseFloat(biomarker.result);
                                                return isNaN(num) ? biomarker.result : num.toFixed(1);
                                            })()}
                                        </span>
                                    )}
                                    {parseNumericValue(biomarker.result) !== null && biomarker.units && (
                                        <span className="text-xs text-gray-500">{biomarker.units}</span>
                                    )}
                                </div>
                            </div>

                            {/* Column 2: Trend mini-chart — framed box, status-tinted
                                area fill, navy curve + dot at the latest reading. */}
                            <div className="flex justify-center min-w-0">
                                <div className="h-14 w-full overflow-hidden rounded-lg border border-gray-200 bg-white">
                                    {biomarker.hasHistory ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart
                                                data={trendGrid.data}
                                                margin={{top: 4, right: 0, left: 0, bottom: 0}}>
                                                <defs>
                                                    <linearGradient
                                                        id={chartGradientId}
                                                        x1="0"
                                                        y1="0"
                                                        x2="0"
                                                        y2="1">
                                                        <stop
                                                            offset="5%"
                                                            stopColor={gradientColors.primary}
                                                            stopOpacity={0.35}
                                                        />
                                                        <stop
                                                            offset="95%"
                                                            stopColor={gradientColors.secondary}
                                                            stopOpacity={0.04}
                                                        />
                                                    </linearGradient>
                                                </defs>
                                                <XAxis
                                                    hide
                                                    dataKey="x"
                                                    type="number"
                                                    domain={trendGrid.domain}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="none"
                                                    fill={`url(#${chartGradientId})`}
                                                    connectNulls={false}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="value"
                                                    stroke="#013B4B"
                                                    strokeWidth={1.5}
                                                    dot={(props: unknown) => {
                                                        const {cx, cy, payload} = props as {
                                                            cx?: number;
                                                            cy?: number;
                                                            payload?: {current?: boolean; edge?: boolean};
                                                        };
                                                        if (payload?.edge || cx == null || cy == null) {
                                                            return <g />;
                                                        }
                                                        // Vertical line from each marker DOWN to the
                                                        // chart bottom (frame is h-14 = 56px); the dot
                                                        // renders only on the latest reading.
                                                        return (
                                                            <g>
                                                                <line
                                                                    x1={cx}
                                                                    y1={cy}
                                                                    x2={cx}
                                                                    y2={56}
                                                                    stroke="#E5E7EB"
                                                                    strokeWidth={1}
                                                                />
                                                                {payload?.current && (
                                                                    <circle
                                                                        cx={cx}
                                                                        cy={cy}
                                                                        r={3.5}
                                                                        fill="#013B4B"
                                                                    />
                                                                )}
                                                            </g>
                                                        );
                                                    }}
                                                    activeDot={false}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full w-full" />
                                    )}
                                </div>
                            </div>

                            {/* Column 3: Status text + Results link */}
                            <div className="flex flex-col items-end gap-1 min-w-0">
                                <div
                                    className={`flex items-center gap-1 whitespace-nowrap text-sm font-medium ${statusTextClass}`}>
                                    <span>{statusText}</span>
                                    {isOptimal ? (
                                        <Icon name="check-solid" className="h-3.5 w-3.5" />
                                    ) : (
                                        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-current text-[10px] font-bold leading-none">
                                            !
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleResultsClick(biomarker);
                                    }}
                                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-mld-text-primary transition-colors cursor-pointer">
                                    <Icon name="magnifying-glass" className="h-3 w-3" />
                                    <span>Results</span>
                                </button>
                            </div>
                        </div>

                        {/* Mobile Layout: Two Rows */}
                        <div className="md:hidden space-y-3">
                            {/* First Row: Test name, result, status, and results link */}
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-950 mb-1">
                                        {cleanBiomarkerName(biomarker.test_name)}
                                    </h4>
                                    <div className="flex items-baseline space-x-1">
                                        {isTestNotPerformed(biomarker.result) ? (
                                            <span className="text-[17px] text-gray-400 leading-none">Not Tested</span>
                                        ) : (
                                            <span className="text-lg font-semibold text-blue-950">
                                                {(() => {
                                                    const num = parseFloat(biomarker.result);
                                                    return isNaN(num) ? biomarker.result : num.toFixed(1);
                                                })()}
                                            </span>
                                        )}
                                        {parseNumericValue(biomarker.result) !== null && biomarker.units && (
                                            <span className="text-lg font-semibold text-blue-950">
                                                {biomarker.units}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-blue-950">{statusText}</span>
                                        {/* Badge shape driven by the canonical severity tier; stroke +
                                            ring color resolve through getStatusColor/getStatusClass so
                                            the badge always matches the chart/chip/count colors. */}
                                        {severityTier(status) === 'normal' ? (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-normal flex items-center justify-center">
                                                <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                                    <path
                                                        d="M1 3L2.5 4.5L7 0.5"
                                                        stroke={getStatusColor(status)}
                                                        strokeWidth="1.2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : severityTier(status) === 'critical' || severityTier(status) === 'borderline' ? (
                                            <div
                                                className={`w-4 h-4 rounded-full bg-white border-2 ${getStatusClass(status, 'border')} flex items-center justify-center`}>
                                                <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                    <path
                                                        d="M1 0V6.5M1 8.5V9.5"
                                                        stroke={getStatusColor(status)}
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-500 flex items-center justify-center">
                                                <svg width="2" height="2" viewBox="0 0 2 2" fill="none">
                                                    <circle cx="1" cy="1" r="0.8" fill="gray" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleResultsClick(biomarker)}
                                        className="flex items-center space-x-1 text-sm text-blue-950 hover:text-mld-info transition-colors cursor-pointer">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                        <span>Results</span>
                                    </button>
                                </div>
                            </div>

                            {/* Second Row: Historical trend chart */}
                            <div className="w-full">
                                <div className="h-16 w-full rounded-lg border-mld-panel-border border overflow-hidden">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart
                                            data={trendGrid.data}
                                            margin={{top: 6, right: 0, left: 0, bottom: 0}}>
                                            <defs>
                                                <linearGradient
                                                    id={`${chartGradientId}-mobile`}
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1">
                                                    <stop
                                                        offset="5%"
                                                        stopColor={gradientColors.primary}
                                                        stopOpacity={0.3}
                                                    />
                                                    <stop
                                                        offset="95%"
                                                        stopColor={gradientColors.secondary}
                                                        stopOpacity={0.05}
                                                    />
                                                </linearGradient>
                                            </defs>
                                            <XAxis hide dataKey="x" type="number" domain={trendGrid.domain} />
                                            <Area
                                                type="monotone"
                                                dataKey="value"
                                                stroke="none"
                                                fill={`url(#${chartGradientId}-mobile)`}
                                                connectNulls={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="value"
                                                stroke="#013B4B"
                                                strokeWidth={1.5}
                                                dot={(props: unknown) => {
                                                    const {cx, cy, payload} = props as {
                                                        cx?: number;
                                                        cy?: number;
                                                        payload?: {current?: boolean; edge?: boolean};
                                                    };
                                                    if (payload?.edge || cx == null || cy == null) {
                                                        return <g />;
                                                    }
                                                    // Vertical line per marker to the chart bottom
                                                    // (frame is h-16 = 64px); dot on latest reading.
                                                    return (
                                                        <g>
                                                            <line
                                                                x1={cx}
                                                                y1={cy}
                                                                x2={cx}
                                                                y2={64}
                                                                stroke="#E5E7EB"
                                                                strokeWidth={1}
                                                            />
                                                            {payload?.current && (
                                                                <circle cx={cx} cy={cy} r={3.5} fill="#013B4B" />
                                                            )}
                                                        </g>
                                                    );
                                                }}
                                                activeDot={false}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default BiomarkerDetailChart;
